import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MessageType } from 'enums/messages';
import type { RootState } from 'store';
import { tasksApi } from 'store/tasks/tasksSlice';

import { SlidingPaneMode, SlidingPaneProps } from './slidingInPane.types';
import { getSlidingPaneMode } from './slidingPane.settings';

const initialState: SlidingPaneProps = {
  alertsState: {
    [MessageType.Medical]: true,
    [MessageType.SMS]: true,
    [MessageType.StaffNote]: true,
    [MessageType.Support]: true
  },
  isDisabledMarkAsComplete: true,
  isMulticolumnAllowed: false,
  isOpen: false,
  isQueue: false,
  isTaskReminder: true,
  mode: SlidingPaneMode.ORDINARY,
  taskId: ''
};

const slidingPaneSlice = createSlice({
  extraReducers: (builder) => {
    builder.addMatcher(tasksApi.endpoints.getTaskDetails.matchFulfilled, (state, { payload }) => {
      const newMode = getSlidingPaneMode(payload?.category);
      const allowMode =
        newMode === SlidingPaneMode.AI_MESSAGE_PANEL || !!state.isMulticolumnAllowed;
      if (allowMode) state.mode = newMode;
    });
  },
  initialState,
  name: 'slidingPane',
  reducers: {
    closeAlert(state, action: PayloadAction<{ type: MessageType }>) {
      state.alertsState[action.payload.type] = false;
    },
    closeRemindAlert: (state) => {
      state.isTaskReminder = false;
    },
    disableMarkAsComplete: (state, action) => {
      state.isDisabledMarkAsComplete = action.payload;
    },
    open: (state, action) => {
      state.isOpen = true;
      state.isTaskReminder = true;
      state.isQueue = !!action.payload.isQueue;
      if (action.payload.taskId) state.taskId = action.payload.taskId;
    },
    reset: (state) => {
      const currentMode = state.mode;
      const currentIsMulticolumnAllowed = state.isMulticolumnAllowed;
      return {
        ...initialState,
        isMulticolumnAllowed: currentIsMulticolumnAllowed,
        mode: currentMode
      };
    },
    setIsMulticolumnAllowed: (state, action) => {
      state.isMulticolumnAllowed = action.payload;
    },
    setSlidingMode: (state, action) => {
      const allowMode =
        action.payload === SlidingPaneMode.AI_MESSAGE_PANEL || !!state.isMulticolumnAllowed;
      state.mode = allowMode ? action.payload : SlidingPaneMode.ORDINARY;
    }
  }
});

export const selectSlidingInPane = (state: RootState) => state.slidingInPane;

export const {
  closeAlert,
  closeRemindAlert,
  disableMarkAsComplete,
  open,
  reset,
  setIsMulticolumnAllowed,
  setSlidingMode
} = slidingPaneSlice.actions;

export default slidingPaneSlice.reducer;
